import React from 'react';

import { Document, Text, View } from '@react-pdf/renderer';

import { fmtDate, fmtQty, koliFor, sum } from '../format';
import { carrierLabel, serviceLabel } from '../status';
import type { GoodsIssueDetail } from '../types';
import {
  PdfA4,
  PdfBlock,
  type PdfColumn,
  PdfFooter,
  PdfHeader,
  PdfKV,
  PdfKVRow,
  PdfSignatures,
  PdfTable,
  pdfStyles,
  type PrintMeta,
  T,
} from './pdfKit';

/**
 * Surat jalan (GI READY_TO_PICKUP and later): SJ number + QR, sender (client
 * d/a hub), recipient, refs (OO, client ref, GI, AWB once it exists), goods
 * lines with qty, UoM, koli and weight + totals; warehouse / courier /
 * recipient signatures.
 */

export interface DeliveryNoteRow {
  sku: string;
  itemName: string;
  uomName: string;
  qty: number;
  koli: number;
  /** null when the SKU has no weight in the catalog. */
  weightKg: number | null;
}

/**
 * Aggregate GI lines (one per bin/batch) into one row per SKU. The per-unit
 * weight comes with each GI line (`weightKg`, from the SKU catalog).
 */
export const deliveryNoteRows = (
  detail: GoodsIssueDetail
): DeliveryNoteRow[] => {
  const by = new Map<string, DeliveryNoteRow>();
  const unitWeight = new Map<string, number | null>();
  detail.lines.forEach((l) => {
    const qty = l.qtyPicked || l.qtyReserved;
    if (l.weightKg != null || !unitWeight.has(l.sku))
      unitWeight.set(l.sku, l.weightKg ?? null);
    const cur = by.get(l.sku);
    if (cur) cur.qty += qty;
    else
      by.set(l.sku, {
        sku: l.sku,
        itemName: l.itemName,
        uomName: l.uomName,
        qty,
        koli: 0,
        weightKg: null,
      });
  });
  return Array.from(by.values()).map((r) => {
    const unit = unitWeight.get(r.sku);
    return {
      ...r,
      koli: koliFor(r.uomName, r.qty),
      weightKg: unit != null ? Math.round(r.qty * unit * 100) / 100 : null,
    };
  });
};

const DeliveryNotePdf: React.FC<{
  detail: GoodsIssueDetail;
  rows: DeliveryNoteRow[];
  hubAddress: string | null;
  meta: PrintMeta;
}> = ({ detail, rows, hubAddress, meta }) => {
  const gi = detail.goodsIssue;
  const oo = detail.outboundOrder;
  const sj = gi.suratJalanNo ?? '—';
  const hubName = gi.hub?.name ?? `Hub #${gi.hubID}`;
  const waybill = gi.waybill ?? oo?.fulfillment?.waybill ?? null;
  const knownWeights = rows.filter((r) => r.weightKg !== null);
  const totalWeight = knownWeights.length
    ? sum(knownWeights.map((r) => r.weightKg as number))
    : null;

  const columns: PdfColumn<DeliveryNoteRow>[] = [
    { title: 'No', width: 20, fixed: true, render: (_r, i) => <T>{i + 1}</T> },
    {
      title: 'SKU',
      width: 1.6,
      render: (r) => (
        <T mono bold>
          {r.sku}
        </T>
      ),
    },
    { title: 'Nama barang', width: 3, render: (r) => <T>{r.itemName}</T> },
    {
      title: 'Qty',
      width: 0.8,
      align: 'right',
      render: (r) => <T>{fmtQty(r.qty)}</T>,
    },
    { title: 'UoM', width: 0.8, render: (r) => <T>{r.uomName}</T> },
    {
      title: 'Koli',
      width: 0.7,
      align: 'right',
      render: (r) => <T>{fmtQty(r.koli)}</T>,
    },
    {
      title: 'Berat (kg)',
      width: 1,
      align: 'right',
      render: (r) => <T>{r.weightKg === null ? '—' : fmtQty(r.weightKg)}</T>,
    },
  ];

  return (
    <Document title={`Surat jalan ${sj}`}>
      <PdfA4>
        <PdfHeader
          title="Surat jalan"
          code={sj}
          meta={`Tanggal ${fmtDate(gi.readyAt ?? gi.createdAt)} · ${hubName}`}
          qr={meta.qr}
        />
        <View style={[pdfStyles.row, { marginTop: 12 }]}>
          <PdfBlock title="Pengirim">
            <Text style={pdfStyles.value}>
              {gi.client?.name ?? oo?.client?.name ?? `Client #${gi.clientID}`}
            </Text>
            <Text style={[pdfStyles.small, pdfStyles.muted, { marginTop: 2 }]}>
              d/a {hubName}
              {hubAddress ? ` — ${hubAddress}` : ''}
            </Text>
          </PdfBlock>
          <PdfBlock title="Penerima" last>
            <Text style={pdfStyles.value}>
              {oo ? `${oo.recipientName} · ${oo.recipientPhone}` : '—'}
            </Text>
            <Text style={[pdfStyles.small, pdfStyles.muted, { marginTop: 2 }]}>
              {oo?.destinationAddress ?? ''}
              {oo?.destinationNotes ? `\nCatatan: ${oo.destinationNotes}` : ''}
            </Text>
          </PdfBlock>
        </View>
        {/* Two rows of three: codes are long and must not crowd each other. */}
        <PdfKVRow>
          <PdfKV
            label="Outbound request"
            value={oo?.code ?? gi.reference}
            mono
          />
          <PdfKV label="Ref client" value={oo?.externalID} mono />
          <PdfKV label="Picking order" value={gi.code} mono />
        </PdfKVRow>
        <PdfKVRow>
          <PdfKV
            label="Waybill"
            value={waybill ?? 'terbit saat submit shipping'}
            mono={!!waybill}
          />
          <PdfKV
            label="Kurir"
            value={carrierLabel(oo?.fulfillment?.carrier)}
          />
          <PdfKV
            label="Service"
            value={oo ? serviceLabel(oo.serviceType) : null}
          />
        </PdfKVRow>
        <PdfTable columns={columns} rows={rows} />
        <View style={pdfStyles.totals}>
          <Text style={{ fontFamily: 'Helvetica-Bold', marginLeft: 18 }}>
            Total qty {fmtQty(sum(rows.map((r) => r.qty)))}
          </Text>
          <Text style={{ fontFamily: 'Helvetica-Bold', marginLeft: 18 }}>
            Total koli {fmtQty(sum(rows.map((r) => r.koli)))}
          </Text>
          <Text style={{ fontFamily: 'Helvetica-Bold', marginLeft: 18 }}>
            Total berat{' '}
            {totalWeight === null ? '—' : `${fmtQty(totalWeight)} kg`}
          </Text>
        </View>
        {oo?.notes ? (
          <Text style={[pdfStyles.small, { marginTop: 8 }]}>
            <Text style={pdfStyles.label}>Catatan order </Text>
            {oo.notes}
          </Text>
        ) : null}
        <Text style={pdfStyles.note}>
          Barang diterima dalam keadaan baik dan jumlah sesuai, kecuali dicatat
          di surat jalan ini.
        </Text>
        <PdfSignatures
          labels={['Dikeluarkan gudang', 'Kurir / rider', 'Penerima']}
        />
        <PdfFooter
          doc={`Surat jalan ${sj}`}
          printedAt={meta.printedAt}
          printedBy={meta.printedBy}
        />
      </PdfA4>
    </Document>
  );
};

export default DeliveryNotePdf;
